import {
  PaymentType, PayoffV2Payload, PayoffV2Response, PayoffV2VerificationFields, RawPayoffResponse, SavedCardType,
  VerifiedPayoffV2Payload
} from "../../types";
import {formatCurrency} from "../../utils";
import {missingApiValue} from "./theme";
import {CardDestinationView, WithdrawReceiptSnapshot, WithdrawResultState} from "./types";

type PayoffErrorLike = {
  code?: string, message?: string,
  response?: {
    status?: number, data?: { code?: string; details?: string; field?: string; message?: string; reason?: string }
  }
};

type WithdrawFlowErrorLike = {
  code?: string, response?: { status?: number, data?: { code?: unknown, field?: unknown, reason?: unknown } }
};

export type WithdrawalIntentSignature = { amountCents: number, paymentType: PaymentType, destinationId: string };

type WithdrawalAttemptStatus = 'ready' | 'uncertain';

export type WithdrawalAttempt = {
  idempotencyKey: string, signature: WithdrawalIntentSignature, status: WithdrawalAttemptStatus
};

export type PayoffReceiptSnapshotResolution = {
  snapshot: WithdrawReceiptSnapshot, source: 'payoff-response' | 'legacy-quote' | 'invalid-response'
};

export const buildSafeWithdrawFlowErrorLog = (err: unknown) => {
  const error = err as WithdrawFlowErrorLike;
  const data = error.response?.data;
  const code = typeof data?.code === 'string' ? data.code : error.code;
  const field = typeof data?.field === 'string' ? data.field : undefined;
  const reason = typeof data?.reason === 'string' || typeof data?.reason === 'number' ? data.reason : undefined;

  return {status: error.response?.status, code, field, reason};
};

const unknownProcessingMessage = 'Your withdrawal is being processed. Please check your transaction history for updates.';
const payoffTimeoutMessage = 'We could not confirm your withdrawal status. Please try again. We will not submit a duplicate withdrawal.';
const genericPayoffErrorMessage = 'Your withdrawal could not be completed. Please try again or contact support.';
const payoffServiceUnavailableMessage = 'Withdrawals are temporarily unavailable. Please try again later.';
const otpVerificationErrorCodes = [
  'OTP_VERIFICATION_REQUIRED', 'OTP_VERIFICATION_NOT_FOUND', 'OTP_INVALID', 'OTP_EXPIRED', 'OTP_TOO_MANY_ATTEMPTS',
  'OTP_VERIFICATION_MISMATCH', 'OTP_RESEND_TOO_SOON', 'OTP_NO_DELIVERY_CHANNEL'
];
const terminalOtpVerificationErrorCodes = [
  'OTP_VERIFICATION_NOT_FOUND', 'OTP_EXPIRED', 'OTP_TOO_MANY_ATTEMPTS', 'OTP_VERIFICATION_MISMATCH'
];

export const buildWithdrawalIntentSignature = (amountCents: number, paymentType: PaymentType, destinationId = ''): WithdrawalIntentSignature => ({
  amountCents, paymentType, destinationId: paymentType === 'CARD' ? destinationId : ''
});

export const createWithdrawalAttempt = (signature: WithdrawalIntentSignature, idempotencyKey: string): WithdrawalAttempt => ({
  idempotencyKey, signature, status: 'ready'
});

export const isSameWithdrawalIntentSignature = (left: WithdrawalIntentSignature, right: WithdrawalIntentSignature) => (
  left.amountCents === right.amountCents && left.paymentType === right.paymentType && left.destinationId === right.destinationId
);

export const isValidCentsAmount = (value?: number | null): value is number => {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
};

export const formatPayoutCentsAmount = (amountCents?: number | null) => {
  if (!isValidCentsAmount(amountCents)) return missingApiValue;
  return formatCurrency(amountCents / 100);
};

export const formatPayoutFeeLabel = (feeAmountCents?: number | null) => {
  if (!isValidCentsAmount(feeAmountCents)) return missingApiValue;
  return feeAmountCents === 0 ? 'Free' : formatPayoutCentsAmount(feeAmountCents);
};

export const resolvePayoffReceiptSnapshot = (payoff: RawPayoffResponse, quotedSnapshot: WithdrawReceiptSnapshot, submittedAmountCents: number): PayoffReceiptSnapshotResolution => {
  const {amountCents, feeAmountCents, netAmountCents} = payoff;
  const responseHasNoAmountFields = amountCents == null && feeAmountCents == null && netAmountCents == null;

  if (responseHasNoAmountFields) {
    return {snapshot: quotedSnapshot, source: 'legacy-quote'};
  }

  const hasValidAuthoritativeAmounts = isValidCentsAmount(amountCents) && isValidCentsAmount(feeAmountCents) && isValidCentsAmount(netAmountCents) && amountCents === submittedAmountCents && feeAmountCents <= amountCents && netAmountCents === amountCents - feeAmountCents;

  if (!hasValidAuthoritativeAmounts) {
    return {snapshot: quotedSnapshot, source: 'invalid-response'};
  }

  return {
    source: 'payoff-response',
    snapshot: {
      ...quotedSnapshot, amountValue: amountCents / 100, feeLabel: formatPayoutFeeLabel(feeAmountCents),
      netAmountLabel: formatPayoutCentsAmount(netAmountCents)
    }
  };
};

export const buildPayoffV2Payload = (amount: number, paymentType: PaymentType, selectedDestination?: CardDestinationView): PayoffV2Payload => {
  const payload: PayoffV2Payload = {amount, paymentType};

  if (paymentType === 'CARD' && selectedDestination?.id) {
    payload.cardUid = selectedDestination.id;
  }

  return payload;
};

export const buildVerifiedPayoffV2Payload = (amount: number, paymentType: PaymentType, selectedDestination: CardDestinationView | undefined, verification: PayoffV2VerificationFields): VerifiedPayoffV2Payload => {
  return {
    ...buildPayoffV2Payload(amount, paymentType, selectedDestination), verificationId: verification.verificationId,
    otpCode: verification.otpCode
  };
};

export const getPayoffErrorCode = (err: unknown) => {
  const error = err as PayoffErrorLike;
  return error.response?.data?.code || error.response?.data?.reason || error.code || '';
};

export const isRequestTimeout = (err: unknown) => (err as PayoffErrorLike).code === 'ECONNABORTED';

/**
 * A gateway gave up before paygate answered (SMG-4797): nginx's 504, or a 502 from a proxy between us.
 * The body is the gateway's, so there is no paygate code to read, and paygate may still be working —
 * classify it with this app's own timeout, never as a refusal of what the user sent.
 */
export const isGatewayTimeout = (err: unknown) => {
  const status = (err as PayoffErrorLike).response?.status;
  return status === 504 || status === 502;
};

export const isPayoffVerificationError = (err: unknown) => otpVerificationErrorCodes.includes(getPayoffErrorCode(err));

export const isTerminalPayoffVerificationError = (err: unknown) => terminalOtpVerificationErrorCodes.includes(getPayoffErrorCode(err));

export const getPayoffVerificationErrorMessage = (err: unknown) => {
  switch (getPayoffErrorCode(err)) {
    case 'OTP_INVALID':
      return 'That code is not correct. Please try again.';
    case 'OTP_EXPIRED':
      return 'That code expired. Please request a new code.';
    case 'OTP_TOO_MANY_ATTEMPTS':
      return 'Too many incorrect attempts. Please request a new code or contact support.';
    case 'OTP_VERIFICATION_MISMATCH':
      return 'This code does not match the withdrawal details. Please restart verification.';
    case 'OTP_VERIFICATION_NOT_FOUND':
      return 'We could not find this verification. Please request a new code.';
    case 'OTP_RESEND_TOO_SOON':
      return 'Please wait before requesting another code.';
    case 'OTP_NO_DELIVERY_CHANNEL':
      return 'We could not send a verification code. Please check your phone or email and try again.';
    case 'OTP_VERIFICATION_REQUIRED':
      return 'Enter the verification code to continue.';
    default:
      return 'We could not verify this code. Please try again.';
  }
};

const getCardSuffix = (maskedNumber: string) => {
  const lastFour = maskedNumber.replace(/\D/g, '').slice(-4);
  return lastFour ? `••••${lastFour}` : maskedNumber.slice(-8) || missingApiValue;
};

export const buildCardDestinationView = (card: SavedCardType): CardDestinationView => {
  const maskedNumber = card.cardMaskedNumber || missingApiValue;
  const suffix = getCardSuffix(maskedNumber);

  return {
    id: card.cardUid, icon: 'credit-card', title: `Card ${suffix}`, subtitle: 'Saved payout card',
    summary: `Card ${suffix}`
  };
};

/**
 * A Finix-tokenized card as a selectable payout destination (SMG-4557).
 *
 * The id is the opaque `finix:<id>` handle the endpoint returned, and it travels to the payout as
 * `cardUid` exactly as a `SavedCard`'s `cardUid` does — paygate's `resolvePayoffCard` accepts the
 * `finix:` namespace and re-validates ownership server-side. There is no `SavedCard` row behind it,
 * which is why it cannot come from `getSavedCards()` and has to be registered by the save that
 * created it.
 */
export const buildFinixDestinationView = (destinationRef: string, lastFour?: string): CardDestinationView => {
  const suffix = lastFour ? `••${lastFour}` : missingApiValue;

  return {
    id: destinationRef, icon: 'credit-card', title: `Card ${suffix}`, subtitle: 'Instant payout card',
    summary: `Card ${suffix}`
  };
};

/**
 * A Paysafe-tokenized card as a selectable payout destination (SMG-4057).
 *
 * The id is the opaque `paysafe:<id>` handle the endpoint returned, and it travels to the payout as
 * `cardUid` exactly like a `finix:<id>` ref. The label is deliberately provider-neutral (PS-17): the
 * customer sees a card and its last four, never which processor holds it.
 */
export const buildPaysafeDestinationView = (destinationRef: string, lastFour?: string): CardDestinationView => {
  const suffix = lastFour ? `••${lastFour}` : missingApiValue;

  return {
    id: destinationRef, icon: 'credit-card', title: `Card ${suffix}`, subtitle: 'Instant payout card',
    summary: `Card ${suffix}`
  };
};

const buildProcessingFallbackPayoff = (payoff: RawPayoffResponse): PayoffV2Response => {
  return {...payoff, status: 'PROCESSING', message: unknownProcessingMessage};
};

export const buildWithdrawResultFromPayoff = (payoff: RawPayoffResponse): WithdrawResultState => {
  switch (payoff.status) {
    case 'PAID':
      return {kind: 'success', payoff: {...payoff, status: 'PAID'}};
    case 'PROCESSING':
      return {kind: 'processing', payoff: {...payoff, status: 'PROCESSING'}};
    default:
      return {kind: 'processing', payoff: buildProcessingFallbackPayoff(payoff)};
  }
};

export const getPayoffErrorMessage = (err: unknown) => {
  const error = err as PayoffErrorLike;
  if (isRequestTimeout(error) || isGatewayTimeout(error)) return payoffTimeoutMessage;
  if (error.response?.status === 503) return payoffServiceUnavailableMessage;

  return genericPayoffErrorMessage;
};
